// @mui material components
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import ReportsLineChart from "parts/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "parts/Cards/StatisticsCards/ComplexStatisticsCard";

import StoryCard from "parts/Cards/StoryCard";

import JapanMap from "components/JapanMap";

import { getRandomImages } from "../../utils/getRandomImages";

import { useContext, useEffect, useState, useRef } from "react";
import { LanguageContext } from "context";

import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { voicePlayAudioStreamAuto } from "@shared/api/voice";

import InfoMessageDialog from "components/InfoMessageDialog";
import TodayEntry from "components/futureBoard/TodayEntry";

import {
  getDashboardGraph,
  getDashboardSummaryStats,
  getDashboardJapanMap,
  getDashboardAiComment,
  saveDashboardAiComment,
  type DashboardGraph,
  type DashboardSummary,
  type PrefectureCompletionResult,
  type AiCommentInfo,
} from "@shared/api/dashboard";

function Dashboard(): JSX.Element {
  const { t } = useContext(LanguageContext) as any;
  const { refresh, session } = useSessionContext();
  const { showNotification } = useNotification();

  const [loading, setLoading] = useState(false);

  const [dashboardData, setDashboardData] = useState<DashboardGraph>(null);
  const [summaryStats, setSummaryStats] = useState<DashboardSummary>(null);
  const [japanMapStats, setJapanMap] = useState<PrefectureCompletionResult>(null);
  const [aiGoodComment, setAiGoodComment] = useState<AiCommentInfo>(null);
  const [aiComment, setAicomment] = useState<AiCommentInfo>(null);

  const [images, setImages] = useState<string[]>([]);

  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const fetchDashboardGraph = async () => {
      try {
        if (!session?.access_token) {
          await refresh();
          if (!session?.access_token) {
            return;
          }
        }

        const json = await getDashboardGraph(session.access_token);
        setDashboardData(json);
        console.log("Dashboard data:", json);
      } catch (err) {
        console.error("Dashboardデータ取得エラー:", err);
        showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          2000
        );
      }
    };

    const fetchSummaryStats = async () => {
      try {
        if (!session?.access_token) return;

        const stats = await getDashboardSummaryStats(session.access_token);
        setSummaryStats(stats);
        console.log("Summary stats:", stats);
      } catch (err) {
        console.error("Summary stats取得エラー:", err);
        showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          2000
        );
      }
    };

    const fetchJapanMap = async () => {
      try {
        if (!session?.access_token) return;

        const japanMap = await getDashboardJapanMap(session.access_token);
        console.log("japanMap:", japanMap);
        setJapanMap(japanMap);
      } catch (err) {
        console.error("Japan Map取得エラー:", err);
        showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          2000
        );
      }
    };

    const fetchAiComment = async () => {
      try {
        if (!session?.access_token) return;

        const aiComment = await getDashboardAiComment(session.access_token, 4);
        console.log("ai comment: result");
        setAicomment(aiComment);
      } catch (err) {
        console.error("Ai comment取得エラー:", err);
        showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          2000
        );
      }
    };

    const fetchAiGoodComment = async () => {
      try {
        if (!session?.access_token) return;

        const aiGoodComment = await getDashboardAiComment(session.access_token, 2);
        console.log("ai good comment:result");
        setAiGoodComment(aiGoodComment);
      } catch (err) {
        console.error("Ai good comment取得エラー:", err);
        showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          2000
        );
      }
    };

    fetchAiGoodComment();
    fetchAiComment();
    fetchSummaryStats();
    fetchDashboardGraph();
    fetchJapanMap();
  }, [session?.access_token]);

  useEffect(() => {
    const imgs = getRandomImages(2);
    setImages(imgs);
  }, []);

  const handleThumbFeedback = async (type: "normal" | "good", isUp: boolean) => {
    if (!session?.access_token) return;

    try {
      if (type === "normal" && aiComment) {
        // いいね/バッドの反転処理（排他制御）
        const newThumbUp = isUp ? !aiComment.ai_comment_thumb_up : false;
        const newThumbDown = !isUp ? !aiComment.ai_comment_thumb_down : false;

        // APIを呼び出してデータベースを更新し、返却された最新状態をStateにセットする
        const updated = await saveDashboardAiComment(
          session.access_token,
          aiComment.id,
          newThumbUp,
          newThumbDown
        );
        setAicomment(updated);
      }
      
      if (type === "good" && aiGoodComment) {
        const newThumbUp = isUp ? !aiGoodComment.ai_comment_thumb_up : false;
        const newThumbDown = !isUp ? !aiGoodComment.ai_comment_thumb_down : false;

        const updated = await saveDashboardAiComment(
          session.access_token,
          aiGoodComment.id,
          newThumbUp,
          newThumbDown
        );
        setAiGoodComment(updated);
      }
    } catch (err) {
      console.error("評価の更新エラー:", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.updated-error"),
        },
        2000
      );
    }
  };

  const handleVoicePlay = async (comment_type: string, ai_comments_id: number) => {
    try {
      if (!ai_comments_id || !session?.access_token || !comment_type) return;

      setLoading(true);

      // audioを明示的に停止・リセットしてから再生
      audioRef.current.pause();
      audioRef.current.load();

      await voicePlayAudioStreamAuto(session.access_token, comment_type, ai_comments_id, "morioki", audioRef.current);
    } catch (err) {
      console.error("音声出力エラー:", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("AITalk.error.play"),
        },
        3000
      );
    } finally {
      setLoading(false);
    }
  };

  // Action buttons for the StoryCard
  const actionButtons = (comment_type: string, ai_comments_id: number) => (
    <>
      <Tooltip title={t("DashboardPage.voice-label")} placement="bottom">
        <MDTypography
          variant="body1"
          color="info"
          lineHeight={1}
          sx={{ cursor: "pointer", mx: 3 }}
          onClick={() => handleVoicePlay(comment_type, ai_comments_id)}
        >
          <Icon color="inherit">record_voice_over</Icon>
        </MDTypography>
      </Tooltip>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <InfoMessageDialog />

      {/* 未来ボードの入口（使えない間は何も表示しない） */}
      <TodayEntry />

      <MDBox py={2} sx={{ position: "relative" }}>
        <Grid container spacing={3} alignItems="flex-end">
          <audio ref={audioRef} hidden controls />
          {/* スピナーを重ねる（loading=true のときだけ） */}
          {loading && (
            <Fade in timeout={300}>
              <MDBox
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 9999,
                  backgroundColor: "rgba(255, 255, 255, 0.4)",
                }}
              >
                {/* 内側のBox: 縦位置を画面の中央に固定する */}
                <MDBox
                  sx={{
                    position: "sticky",
                    top: "50vh",                   // 画面の高さの半分（50%）の位置
                    transform: "translateY(-50%)", // 自身の高さの半分だけ上に戻して完全な中央にする
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress color="info" />
                </MDBox>
              </MDBox>
            </Fade>
          )}
          
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mt={3}>
              <StoryCard
                image={images[0]}
                title={aiComment?.ai_comment_title}
                description={aiComment?.ai_comment_comment || ""}
                action={aiComment ? actionButtons("aiComment", aiComment.id) : undefined}
                isThumbUp={aiComment?.ai_comment_thumb_up}
                isThumbDown={aiComment?.ai_comment_thumb_down}
                onThumbUp={aiComment ? () => handleThumbFeedback("normal", true) : undefined}
                onThumbDown={aiComment ? () => handleThumbFeedback("normal", false) : undefined}
                disabledThumb={!aiComment?.ai_comment_comment}
              />
            </MDBox>
          </Grid>
          
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mt={3}>
              <StoryCard
                image={images[1]}
                title={aiGoodComment?.ai_comment_title}
                description={aiGoodComment?.ai_comment_comment || ""}
                action={aiGoodComment ? actionButtons("aiGoodComment", aiGoodComment.id) : undefined}
                isThumbUp={aiGoodComment?.ai_comment_thumb_up}
                isThumbDown={aiGoodComment?.ai_comment_thumb_down}
                onThumbUp={aiGoodComment ? () => handleThumbFeedback("good", true) : undefined}
                onThumbDown={aiGoodComment ? () => handleThumbFeedback("good", false) : undefined}
                disabledThumb={!aiGoodComment?.ai_comment_comment}
              />
            </MDBox>
          </Grid>
          
          <Grid item xs={12} md={6} lg={4}>
            <MDBox
              mt={3}
              display="flex"
              alignItems="flex-end"
              sx={{ minHeight: 180, width: "100%" }}
            >
              {dashboardData && (
                <ReportsLineChart
                  color="info"
                  title={t("DashboardPage.comp-task-title")}
                  description={t("DashboardPage.chart-note")}
                  date={t("DashboardPage.chart-date")}
                  chart={{
                    labels: dashboardData.report_data.labels,
                    datasets: {
                      label: t("DashboardPage.chart-label"),
                      data: dashboardData.report_data.data,
                    },
                  }}
                  sx={{ width: "100%" }}
                />
              )}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <MDBox mt={1.5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="check"
                title={t("DashboardPage.monthly-comp-task-title")}
                count={summaryStats?.total_completed_tasks ?? 0}
                percentage={{
                  color: "info",
                  amount: "",
                  label: t("DashboardPage.completed_label"),
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="access_alarm"
                title={t("DashboardPage.monthly-pomodoro")}
                count={summaryStats?.total_pomodoro_sessions ?? 0}
                percentage={{
                  color: "success",
                  amount: "",
                  label: t("DashboardPage.pomoload_label"),
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="login"
                title={t("DashboardPage.monthly-login")}
                count={summaryStats?.monthly_login_days ?? 0}
                percentage={{
                  color: "dark",
                  amount: "",
                  label: t("DashboardPage.login_label"),
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="error"
                title={t("DashboardPage.deferred-task")}
                count={summaryStats?.overdue_tasks ?? 0}
                percentage={{
                  color: "primary",
                  amount: "",
                  label: t("DashboardPage.delayed_label"),
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <MDBox py={3}>
        <Grid container>
          {japanMapStats && (
            <JapanMap
              completedPrefectures={japanMapStats.completed_prefectures}
              inProgressPrefectures={japanMapStats.in_progress_prefectures}
            />
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;